import TermsRepository from "../repositories/implementations/TermsRepository";
import TranslationsRepository from "../repositories/implementations/TranslationsRepository";
import { Term } from "../entities/Term";
import { Translation } from "../entities/Translation";
import { mapWithConcurrency, splitIntoChunks } from "../utils/batching";

/** Rows per bulk insert request */
const INSERT_CHUNK_SIZE = 500;

/** Single-row updates in flight at the same time */
const UPDATE_CONCURRENCY = 10;

export interface ImportEntry {
  key: string;
  valueOne: string;
  valueOther: string | null;
  isPlural: boolean;
}

export interface ImportError {
  key: string;
  reason: string;
}

/** What importing a file would change, computed against the project's current data. Used as the preview. */
export interface ImportPlan {
  languageCode: string;
  /** Keys that don't exist in the project yet */
  newTerms: ImportEntry[];
  /** Keys that have no translation for this language yet (includes the new terms) */
  newTranslations: ImportEntry[];
  /** Existing translations whose values differ from the file */
  changedTranslations: { translation: Translation; entry: ImportEntry }[];
  /** Existing translations that already match the file */
  unchangedCount: number;
  /** Entries that can't be imported and are skipped */
  conflicts: ImportError[];
}

export interface ImportSummary {
  languageCode: string;
  termsCreated: number;
  translationsCreated: number;
  translationsUpdated: number;
  translationsUnchanged: number;
  errors: ImportError[];
}

/** Project terms by key */
export type TermsByKey = Map<string, Term>;

/** One language's translations by term ID */
export type TranslationsByTermId = Map<string, Translation>;

const isSameValue = (a: string | null, b: string | null) => (a ?? "") === (b ?? "");

class ImportService {
  private termsRepo = new TermsRepository();

  private translationsRepo = new TranslationsRepository();

  /** Parse a raw JSON file text into key-value pairs. Throws on invalid JSON. */
  parseJson(jsonText: string): Record<string, string> {
    let parsed: any;
    try {
      parsed = JSON.parse(jsonText);
    } catch {
      throw new Error("Invalid JSON file. Please check that the file is valid JSON.");
    }

    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      throw new Error("Invalid format. The JSON file must be a flat object of key-value pairs.");
    }

    Object.entries(parsed).forEach(([key, value]) => {
      if (typeof value !== "string") {
        throw new Error(`Invalid value for key "${key}". All values must be strings.`);
      }
      if (!key.trim()) {
        throw new Error("Invalid key. Keys can't be empty.");
      }
    });

    return parsed as Record<string, string>;
  }

  /** Parse a raw JSON file text into import entries. Values with a `|` are plural: "one|other". Throws on invalid JSON. */
  parseEntries(jsonText: string): ImportEntry[] {
    const seenKeys = new Set<string>();
    return Object.entries(this.parseJson(jsonText)).map(([rawKey, value]) => {
      // keys are saved trimmed, so "a" and "a " would be the same term
      const key = rawKey.trim();
      if (seenKeys.has(key)) {
        throw new Error(`Duplicate key "${key}". Keys must be unique, ignoring spaces around them.`);
      }
      seenKeys.add(key);

      const isPlural = value.includes("|");
      if (!isPlural) return { key, valueOne: value, valueOther: null, isPlural };
      const [valueOne, valueOther] = value.split("|");
      return { key, valueOne, valueOther: valueOther ?? "", isPlural };
    });
  }

  async loadTerms(projectId: string): Promise<TermsByKey> {
    const terms = await this.termsRepo.getByProject(projectId);
    return new Map(terms.map((t) => [t.key, t]));
  }

  async loadTranslations(projectId: string, languageCode: string): Promise<TranslationsByTermId> {
    const translations = await this.translationsRepo.getByProjectAndLanguage(projectId, languageCode);
    return new Map(translations.map((t) => [t.termId, t]));
  }

  /** Compares the file's entries with the project's data and works out what an import would change. No requests. */
  buildPlan(
    languageCode: string,
    entries: readonly ImportEntry[],
    termsByKey: TermsByKey,
    translationsByTermId: TranslationsByTermId
  ): ImportPlan {
    const plan: ImportPlan = {
      languageCode: languageCode.toLowerCase(),
      newTerms: [],
      newTranslations: [],
      changedTranslations: [],
      unchangedCount: 0,
      conflicts: [],
    };

    entries.forEach((entry) => {
      const term = termsByKey.get(entry.key);
      const translation = term ? translationsByTermId.get(term.id) : undefined;

      // The export only returns the plural half for terms marked plural, so importing one into a singular term
      // would save a value that never comes back out. Skip it and let the user fix the term.
      if (term && entry.isPlural && !term.isPlural) {
        plan.conflicts.push({
          key: entry.key,
          reason: "The file has a plural value but this term is singular. Change the term to plural in the Terms tab.",
        });
        return;
      }

      if (!term) plan.newTerms.push(entry);

      if (!translation) {
        plan.newTranslations.push(entry);
      } else if (!isSameValue(translation.valueOne, entry.valueOne) || !isSameValue(translation.valueOther, entry.valueOther)) {
        plan.changedTranslations.push({ translation, entry });
      } else {
        plan.unchangedCount += 1;
      }
    });

    return plan;
  }

  /**
   * Applies a plan: bulk-inserts new terms and translations, then updates the changed translations one by one.
   * Adds what it creates or updates to `termsByKey` and `translationsByTermId`, so they stay in sync with the database
   * and the next file's plan can be built from them without reloading.
   */
  async execute(
    projectId: string,
    plan: ImportPlan,
    termsByKey: TermsByKey,
    translationsByTermId: TranslationsByTermId
  ): Promise<ImportSummary> {
    const summary: ImportSummary = {
      languageCode: plan.languageCode,
      termsCreated: 0,
      translationsCreated: 0,
      translationsUpdated: 0,
      translationsUnchanged: plan.unchangedCount,
      errors: [...plan.conflicts],
    };
    const failedKeys = new Set<string>();
    const fail = (keys: string[], error: any) => {
      const reason = error?.message || "Unknown error";
      keys.forEach((key) => {
        failedKeys.add(key);
        summary.errors.push({ key, reason });
      });
    };

    // 1. New terms (sequential chunks, so a failure only affects its own chunk)
    // eslint-disable-next-line no-restricted-syntax
    for (const chunk of splitIntoChunks(plan.newTerms, INSERT_CHUNK_SIZE)) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const created = await this.termsRepo.createMany(
          projectId,
          chunk.map((e) => ({ key: e.key, isPlural: e.isPlural }))
        );
        created.forEach((term) => termsByKey.set(term.key, term));
        summary.termsCreated += created.length;
      } catch (e) {
        fail(
          chunk.map((entry) => entry.key),
          e
        );
      }
    }

    // 2. New translations (skipping keys whose term couldn't be created)
    const translationsToCreate = plan.newTranslations.filter((entry) => !failedKeys.has(entry.key));
    // eslint-disable-next-line no-restricted-syntax
    for (const chunk of splitIntoChunks(translationsToCreate, INSERT_CHUNK_SIZE)) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const created = await this.translationsRepo.createMany(
          plan.languageCode,
          chunk.map((entry) => ({ termId: (termsByKey.get(entry.key) as Term).id, valueOne: entry.valueOne, valueOther: entry.valueOther }))
        );
        created.forEach((translation) => translationsByTermId.set(translation.termId, translation));
        summary.translationsCreated += created.length;
      } catch (e) {
        fail(
          chunk.map((entry) => entry.key),
          e
        );
      }
    }

    // 3. Changed translations, one request each
    await mapWithConcurrency(plan.changedTranslations, UPDATE_CONCURRENCY, async ({ translation, entry }) => {
      try {
        const updated = await this.translationsRepo.update(translation.id, entry.valueOne, entry.valueOther);
        translationsByTermId.set(updated.termId, updated);
        summary.translationsUpdated += 1;
      } catch (e) {
        fail([entry.key], e);
      }
    });

    return summary;
  }
}

export default ImportService;
